import React, { PropsWithChildren } from 'react'
import styled, { css } from 'styled-components'
import { Tooltip, Link } from '@sketch/components'
import { routes } from '@sketch/modules-common'
import { VersionLink } from '../../../../versioning'
import { SymbolMaster } from '../../../../inspector'
import { ReactComponent as LibraryIcon } from '@sketch/icons/link-library-17'

const ExternalLinkIcon = styled(LibraryIcon)`
  width: 17px;
  height: 17px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  flex: none;
  position: absolute;
  right: 24px;
  top: 16px;
  display: none;
`

const HoverContainer = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    width: calc(100% + 40px);
    height: 48px;
    position: relative;
    display: flex;
    align-items: center;
    border-radius: ${theme.radii.large};

    padding-top: 4px;
    padding-bottom: 4px;
    margin-bottom: 0;
    margin-left: -20px;
    padding-left: 20px;
    padding-right: 20px;

    a {
      width: 80%;
    }

    &:hover {
      background-color: ${theme.colors.state.hover};

      ${ExternalLinkIcon} {
        display: block;
      }
    }
  `
)

type FrameDetailInspectorSymbolLinkProps = PropsWithChildren<{
  symbolMaster: SymbolMaster | null
  frameUuid?: string
  shareIdentifier?: string
  isSymbolLink?: boolean
}>

export function FrameDetailInspectorSymbolLink({
  symbolMaster,
  shareIdentifier,
  frameUuid,
  children,
  isSymbolLink,
}: FrameDetailInspectorSymbolLinkProps) {
  // Data is not consistent for now, so we need to check that both exist
  // in order to link to a proper artboard (symbol masters are just arboards)
  if (!symbolMaster || !frameUuid || !shareIdentifier) {
    return <SymbolUnavailable>{children}</SymbolUnavailable>
  }

  // When linking to a foreign symbol we always link to the latest version,
  // but for internal symbols we want a versioned link
  return (
    <HoverContainer>
      {symbolMaster?.isForeign ? (
        <Link
          data-testid="inspector-symbol-link"
          target="_blank"
          to={
            routes.FRAME.create({
              shareID: shareIdentifier,
              frameUUID: frameUuid,
            }) + '#Inspect'
          }
        >
          {children}
        </Link>
      ) : (
        <VersionLink
          target="_blank"
          data-testid="inspector-symbol-link"
          to={{
            routeKey: 'FRAME',
            routeParams: {
              shareID: shareIdentifier,
              frameUUID: frameUuid,
            },
            hash: '#Inspect',
          }}
        >
          {children}
        </VersionLink>
      )}
      {isSymbolLink && (
        <Tooltip placement="top" content="View in Library">
          <ExternalLinkIcon />
        </Tooltip>
      )}
    </HoverContainer>
  )
}

export const SymbolUnavailable: React.FC = ({ children }) => (
  <Tooltip
    placement="top"
    content="This Symbol belongs to a Library that is unavailable"
  >
    {children}
  </Tooltip>
)
